import { useEffect, useRef, type RefObject } from "react";
import { setEdgeGestureExclusion } from "../../lib/edge-gesture.js";
import { useInboxDragStore } from "../../stores/index.js";
import type { DayCol } from "./build-days.js";
import { setCalendarTouchDrag, setCalendarTouchDrop } from "./touch-drop.js";
import { edgePageZonePx, EDGE_PAGE_FIRST_MS, EDGE_PAGE_MS, type TouchGhost } from "./touch-gesture-util.js";

const SNAP_MIN = 15;
const DAY_MIN = 24 * 60;

interface Args {
  enabled: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  allDayRef?: RefObject<HTMLDivElement | null>;
  trackRef: RefObject<HTMLDivElement | null>;
  days: DayCol[];
  visibleCount: number;
  hourHeight: number;
  onDayShift?: ((delta: number) => void) | undefined;
  onDrop: (day: Date, minutesFromMidnight: number, taskId: string) => void;
  onAllDayDrop?: ((day: Date, taskId: string, durationMinutes: number) => void) | undefined;
}

/**
 * Registers the mobile inbox→calendar touch bridge while mounted:
 *  - a DROP resolver — the dragged inbox row's release calls `calendarTouchDrop`,
 *    which maps that viewport point to a day + 15-min slot and schedules;
 *  - a drag-MOVE feed — the row calls `calendarTouchDragMove` on each finger
 *    move; a rAF loop pages the day window while the finger HOLDS in a strip at
 *    the grid's left/right edge (the same dwell-then-repeat cadence as a block
 *    move, so an inbox item can be dragged across weeks).
 *
 * Refs keep the registered closures reading current geometry without
 * re-registering.
 */
export function useCalendarTouchDrop({ enabled, scrollRef, allDayRef, trackRef, days, visibleCount, hourHeight, onDayShift, onDrop, onAllDayDrop }: Args): void {
  const daysRef = useRef(days); daysRef.current = days;
  const hourHeightRef = useRef(hourHeight); hourHeightRef.current = hourHeight;
  const visibleCountRef = useRef(visibleCount); visibleCountRef.current = visibleCount;
  const onDayShiftRef = useRef(onDayShift); onDayShiftRef.current = onDayShift;
  const onDropRef = useRef(onDrop); onDropRef.current = onDrop;
  const onAllDayDropRef = useRef(onAllDayDrop); onAllDayDropRef.current = onAllDayDrop;

  useEffect(() => {
    if (!enabled) return;
    function allDayGhostAt(x: number, y: number): Extract<TouchGhost, { kind: "all-day" }> | null {
      const row = allDayRef?.current;
      if (!row) return null;
      const rowRect = row.getBoundingClientRect();
      if (x < rowRect.left || x > rowRect.right || y < rowRect.top || y > rowRect.bottom) return null;
      const { title } = useInboxDragStore.getState();
      let best: { col: number; rect: DOMRect; dist: number } | null = null;
      for (const el of row.querySelectorAll<HTMLElement>("[data-all-day-col]")) {
        const raw = el.dataset["allDayCol"];
        if (raw === undefined) continue;
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;
        const col = Number(raw);
        if (!Number.isFinite(col)) continue;
        if (x >= rect.left && x < rect.right && y >= rect.top && y <= rect.bottom) {
          const topPx = Math.max(2, Math.min(rowRect.height - 22, y - rowRect.top - 10));
          return { kind: "all-day", col, leftPx: rect.left - rowRect.left, topPx, widthPx: rect.width, title };
        }
        const dx = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
        const dy = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
        const dist = Math.hypot(dx, dy);
        if (!best || dist < best.dist) best = { col, rect, dist };
      }
      if (!best) return null;
      const topPx = Math.max(2, Math.min(rowRect.height - 22, y - rowRect.top - 10));
      return {
        kind: "all-day",
        col: best.col,
        leftPx: best.rect.left - rowRect.left,
        topPx,
        widthPx: best.rect.width,
        title,
      };
    }

    function timedGhostAt(x: number, y: number): Extract<TouchGhost, { kind: "timed" }> | null {
      const scroll = scrollRef.current, track = trackRef.current;
      if (!scroll || !track) return null;
      const sr = scroll.getBoundingClientRect();
      if (x < sr.left || x > sr.right || y < sr.top || y > sr.bottom) return null;
      const tr = track.getBoundingClientRect();
      const ds = daysRef.current;
      const nn = Math.max(1, ds.length);
      const cIdx = Math.max(0, Math.min(nn - 1, Math.floor(((x - tr.left) / tr.width) * nn)));
      const minRaw = ((y - tr.top) / hourHeightRef.current) * 60;
      const minute = Math.max(0, Math.min(DAY_MIN - SNAP_MIN, Math.round(minRaw / SNAP_MIN) * SNAP_MIN));
      const { title, durationMinutes } = useInboxDragStore.getState();
      return { kind: "timed", col: cIdx, topMin: minute, durationMin: Math.max(SNAP_MIN, durationMinutes), title };
    }

    setCalendarTouchDrop((taskId, x, y) => {
      const allDayGhost = allDayGhostAt(x, y);
      if (allDayGhost && onAllDayDropRef.current) {
        const d = daysRef.current[allDayGhost.col]?.date;
        if (!d) return false;
        void onAllDayDropRef.current(new Date(d), taskId, useInboxDragStore.getState().durationMinutes);
        return true;
      }
      const timedGhost = timedGhostAt(x, y);
      if (!timedGhost) return false;
      const d = daysRef.current[timedGhost.col]?.date;
      if (!d) return false;
      void onDropRef.current(new Date(d), timedGhost.topMin, taskId);
      return true;
    });

    // Edge auto-paging while an inbox item is dragged over the grid — mirrors the
    // block-move loop (useTouchCalendarGestures). The finger pos is fed via
    // move(); a rAF loop runs the dwell-then-repeat cadence so HOLDING at the
    // edge keeps paging even when no further touchmove events fire.
    let raf = 0, lx = 0, ly = 0, edgeDir = 0, edgeEnterAt = 0, edgeFiredAt = 0, running = false;
    function loop() {
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (!running) return;
        const scroll = scrollRef.current;
        if (scroll) {
          const rect = scroll.getBoundingClientRect();
          const allDayGhost = allDayGhostAt(lx, ly);
          const timedGhost = timedGhostAt(lx, ly);
          useInboxDragStore.getState().setPreview(allDayGhost ?? timedGhost);
          const allDayRect = allDayRef?.current?.getBoundingClientRect();
          const overAllDay = !!allDayRect && ly >= allDayRect.top && ly <= allDayRect.bottom;
          const over = overAllDay || (ly >= rect.top && ly <= rect.bottom);
          const zone = edgePageZonePx(scroll.clientWidth, Math.max(1, visibleCountRef.current));
          const dir = over ? (lx < rect.left + zone ? -1 : lx > rect.right - zone ? 1 : 0) : 0;
          const now = performance.now();
          if (dir !== edgeDir) { edgeDir = dir; edgeEnterAt = now; edgeFiredAt = 0; }
          if (dir !== 0) {
            const due = edgeFiredAt === 0 ? now - edgeEnterAt >= EDGE_PAGE_FIRST_MS : now - edgeFiredAt >= EDGE_PAGE_MS;
            if (due) { edgeFiredAt = now; onDayShiftRef.current?.(dir); }
          }
        }
        loop();
      });
    }
    function stopLoop() {
      running = false;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      edgeDir = 0; edgeFiredAt = 0;
      useInboxDragStore.getState().setPreview(null);
      // Release the edges so Samsung's Edge Panel / split-window works normally.
      setEdgeGestureExclusion(false);
    }
    setCalendarTouchDrag(
      (x, y) => {
        lx = x; ly = y;
        if (!running) {
          running = true; edgeDir = 0; edgeFiredAt = 0;
          // Claim the screen edges for the duration of the inbox drag (same as a
          // block move) so edge-paging isn't hijacked by the OS edge gesture.
          setEdgeGestureExclusion(true);
          loop();
        }
      },
      stopLoop,
    );

    return () => { setCalendarTouchDrop(null); setCalendarTouchDrag(null, null); stopLoop(); };
  }, [enabled, scrollRef, allDayRef, trackRef]);
}
